// A minimal .xlsx (SpreadsheetML) writer built on JSZip — which the app already
// bundles (see DownloadAllAttachments.jsx). An .xlsx is a zip of XML parts, and
// the one-sheet case we need (a header row, string/number/currency cells) is
// small and deterministic, so we generate it directly rather than pull in a
// heavy spreadsheet library. Strings are written inline (t="inlineStr"), so
// there is no shared-strings table to maintain.
//
// A cell in a row is one of:
//   - string            -> text cell (inline string)
//   - number            -> numeric cell
//   - { v, money:true }  -> numeric cell shown as GBP currency (£#,##0.00)
//   - null | undefined | '' -> omitted (a genuinely empty cell)
//
// The first row is treated as a bold header unless boldFirstRow is false.
import JSZip from 'jszip';

const SHEET_NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';

// Column index (0-based) -> spreadsheet letter (0 -> A, 26 -> AA).
function colLetter(index) {
    let n = index;
    let letters = '';
    do {
        letters = String.fromCharCode(65 + (n % 26)) + letters;
        n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return letters;
}

function escapeXml(s) {
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

// One <c> element, or '' when the cell is empty. `bold` applies to string cells
// in the header row. The address (r=) is always the true column letter, so an
// omitted empty cell never lets a later value slide into its column.
function cellXml(ref, cell, bold) {
    if (cell === null || cell === undefined || cell === '') return '';
    let value = cell;
    let money = false;
    if (typeof cell === 'object') {
        value = cell.v;
        money = !!cell.money;
    }
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'number' && Number.isFinite(value)) {
        const style = money ? ' s="2"' : '';
        return `<c r="${ref}"${style}><v>${value}</v></c>`;
    }
    const style = bold ? ' s="1"' : '';
    return `<c r="${ref}" t="inlineStr"${style}><is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`;
}

// Rows (an array of cell arrays) -> the worksheet XML string.
export function sheetXml(rows, { boldFirstRow = true } = {}) {
    const body = (rows || []).map((row, r) => {
        const bold = boldFirstRow && r === 0;
        const cells = (row || []).map((cell, c) => cellXml(`${colLetter(c)}${r + 1}`, cell, bold)).join('');
        return `<row r="${r + 1}">${cells}</row>`;
    }).join('');
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n`
        + `<worksheet xmlns="${SHEET_NS}"><sheetData>${body}</sheetData></worksheet>`;
}

// styles.xml: two fonts (normal, bold) and one custom currency number format.
// cellXfs indexes: 0 default, 1 bold, 2 currency.
const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="${SHEET_NS}">
<numFmts count="1"><numFmt numFmtId="164" formatCode="&quot;£&quot;#,##0.00"/></numFmts>
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
<fills count="1"><fill><patternFill patternType="none"/></fill></fills>
<borders count="1"><border/></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="3"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/><xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/></cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

const CONTENT_TYPES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;

const ROOT_RELS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

const WORKBOOK_RELS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

// Sheet names are capped at 31 chars and cannot contain []*/\?: — Excel refuses
// the file otherwise.
function safeSheetName(name) {
    return escapeXml(String(name || 'Sheet1').replace(/[[\]*/\\?:]/g, ' ').slice(0, 31) || 'Sheet1');
}

function workbookXml(sheetName) {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="${SHEET_NS}" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="${safeSheetName(sheetName)}" sheetId="1" r:id="rId1"/></sheets>
</workbook>`;
}

// Rows -> a downloadable .xlsx Blob.
export function rowsToXlsxBlob(rows, { sheetName = 'Sheet1', boldFirstRow = true } = {}) {
    const zip = new JSZip();
    zip.file('[Content_Types].xml', CONTENT_TYPES_XML);
    zip.file('_rels/.rels', ROOT_RELS_XML);
    zip.file('xl/workbook.xml', workbookXml(sheetName));
    zip.file('xl/_rels/workbook.xml.rels', WORKBOOK_RELS_XML);
    zip.file('xl/styles.xml', STYLES_XML);
    zip.file('xl/worksheets/sheet1.xml', sheetXml(rows, { boldFirstRow }));
    return zip.generateAsync({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
}
